/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/no-multi-comp */
import React, { useState } from 'react';
import { SvgIcon, SelectChangeEvent } from '@mui/material';
import { ArrowDropDown, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, InputAdornment, Menu, MenuItem, OutlinedInput, Select } from '~/UIKit';
import { IFitoListItem } from '../models';
import { IListAction } from './ListAction.models';
import { useListToolbar } from './useListToolbar';
import { Sort } from '../../Icons/Sort';
import { RearrangeStatus } from '../models/RearrangeStatus.enum';
import classnames from 'classnames';

export interface IListToolbarProps {
  items: IFitoListItem[];
  onSearch?: (text: string) => void;
  isSelectionActive?: boolean;
  onRearrange?: (status: RearrangeStatus) => void;
  onSelectAll?: () => void;
  bulkActions?: IListAction[];
  actions?: IListAction[];
  multiSelect?: boolean;
  size?: 'small';
  resetSearch?: boolean;
  sortOptions?: { key: string; label: string }[];
  onSortSelect?: (event: SelectChangeEvent) => void;
  filterComponent?: React.ReactNode;
  searchTextPlaceholder?: string;
  moreActions?: JSX.Element;
}

export const ListToolbar = ({
  items,
  onSearch,
  onSelectAll,
  onRearrange,
  bulkActions,
  actions,
  multiSelect = false,
  isSelectionActive = false,
  size,
  resetSearch = false,
  sortOptions,
  onSortSelect,
  filterComponent,
  searchTextPlaceholder,
  moreActions,
}: IListToolbarProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const mountMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
  };

  const {
    searchText,
    updateSearchText,
    startRearranging,
    stopRearranging,
    saveRearranging,
    isAllSelected,
    isIndeterminate,
    classes,
    show,
  } = useListToolbar({
    multiSelect,
    items,
    onSearch,
    onSelectAll,
    onRearrange,
    bulkActions,
    actions,
    isSelectionActive,
    resetSearch,
    sortOptions,
  });
  const showToolbar = Object.values(show).some(Boolean);
  const shouldShowActionsSection =
    filterComponent || sortOptions || show.bulkActions || show.rearrange || show.rearranging;
  const shouldShowSearchAndSelect = (multiSelect && show.select) || show.search;

  return (
    <div
      className={classnames(classes.container, {
        [classes.show]: showToolbar,
        [classes.small]: size === 'small',
      })}
      data-testid="toolbar-container"
    >
      {shouldShowSearchAndSelect && (
        <div className={classes.searchWrapper}>
          {multiSelect && show.select && (
            <label className={classes.selectionBox} data-testid="selectBox">
              <Checkbox
                checked={isAllSelected}
                classes={{
                  root: classes.selectAllCheckboxRoot,
                  indeterminate: classes.selectAllCheckboxIndeterminate,
                }}
                color="primary"
                indeterminate={isIndeterminate}
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={onSelectAll}
              />
            </label>
          )}
          <div
            className={classnames(classes.searchAndSelectWrapper, {
              [classes.moreActions]: !!moreActions,
            })}
          >
            {show.search && (
              <div className={classes.searchInputContainer} data-testid="search">
                <OutlinedInput
                  className={classes.searchInput}
                  onChange={(e) => updateSearchText(e.currentTarget.value)}
                  placeholder={searchTextPlaceholder}
                  startAdornment={
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  }
                  value={searchText}
                />
              </div>
            )}
            {!!moreActions && moreActions}
          </div>
        </div>
      )}
      {shouldShowActionsSection && (
        <div className={classes.filtersWrapper}>
          {filterComponent && <div className={classes.filterContainer}>{filterComponent}</div>}
          {sortOptions && (
            <div className={classes.sortContainer}>
              <Select
                newDesign
                className={classes.sortSelect}
                data-testid="TableSortSelected"
                defaultValue="CreatedDate"
                onChange={onSortSelect}
              >
                {sortOptions.map(({ key, label }) => (
                  <MenuItem key={key} value={key} className={classes.menuItems}>
                    {t(label)}
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}
          {show.rearrange && (
            <div className={classes.actionsContainer} data-testid="rearrange">
              <Button className={classes.actionButton} onClick={startRearranging}>
                {t('app.sort')}
                <Sort />
              </Button>
            </div>
          )}
          {show.rearranging && (
            <div
              className={`${classes.actionsContainer} ${classes.actionsActive}`}
              data-testid="rearranging"
            >
              <Button className={classes.actionButtonCancel} onClick={stopRearranging}>
                {t('app.cancel')}
              </Button>
              <Button className={classes.actionButtonConfirm} onClick={saveRearranging}>
                {t('app.checkin.select.done')}
              </Button>
            </div>
          )}
          {show.bulkActions && (
            <div
              className={`${classes.actionsContainer} ${classes.actionsActive}`}
              data-testid="bulkactions"
            >
              <Button
                aria-controls="simple-menu"
                aria-haspopup="true"
                className={classes.menuButton}
                onClick={mountMenu}
              >
                {t('app.customerList.actions')}
                <SvgIcon>
                  <ArrowDropDown />
                </SvgIcon>
              </Button>
              <Menu anchorEl={anchorEl} keepMounted onClick={closeMenu} open={Boolean(anchorEl)}>
                {bulkActions?.map((action, index) => (
                  <MenuItem key={`bulkActions_${index}`} onClick={() => action.onClick()}>
                    {action.title}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
